import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ApprovalQueueTask } from "serviceStores/mock/approval-queue.mock";
import type { Plant } from "serviceStores/mock/plants.mock";

/**
 * Payment Queue Store
 *
 * Manages the payment queue view - approved POs pending payment.
 * Used by accounts payable (Allie's queue).
 */
class PaymentQueueStore {
  rootStore: RootStore;

  // Filters
  plantFilter: number | null = null;
  vendorFilter: string = "";
  searchTerm: string = "";
  statusFilter: "all" | "pending" | "completed" = "pending";
  priorityFilter: "all" | "urgent" | "normal" = "all";
  dueDateFilter: "all" | "overdue" | "thisWeek" | "thisMonth" = "all";

  // Sort
  sortField: "dueDate" | "totalAmount" | "vendorName" | "createdDate" =
    "dueDate";
  sortDirection: "asc" | "desc" = "asc";

  // UI State
  loading = false;
  error: string | null = null;
  selectedTaskIds: number[] = [];

  // Reference Data
  plants: Plant[] = [];
  tasks: ApprovalQueueTask[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Date Helpers ====================

  get today(): string {
    return new Date().toISOString().split("T")[0];
  }

  get endOfWeek(): string {
    const date = new Date();
    const dayOfWeek = date.getDay();
    const daysUntilSunday = 7 - dayOfWeek;
    date.setDate(date.getDate() + daysUntilSunday);
    return date.toISOString().split("T")[0];
  }

  get endOfMonth(): string {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date.toISOString().split("T")[0];
  }

  // ==================== Filtered & Sorted Data ====================

  get filteredTasks(): ApprovalQueueTask[] {
    let filtered = this.tasks.filter((t) => t.queueType === "payment");

    // Status filter
    if (this.statusFilter === "pending") {
      filtered = filtered.filter((t) => t.status === "pending");
    } else if (this.statusFilter === "completed") {
      filtered = filtered.filter((t) => t.status === "completed");
    }

    // Due date filter
    if (this.dueDateFilter !== "all") {
      filtered = filtered.filter((t) => {
        if (!t.dueDate) return false;
        switch (this.dueDateFilter) {
          case "overdue":
            return t.dueDate < this.today;
          case "thisWeek":
            return t.dueDate >= this.today && t.dueDate <= this.endOfWeek;
          case "thisMonth":
            return t.dueDate >= this.today && t.dueDate <= this.endOfMonth;
          default:
            return true;
        }
      });
    }

    // Plant filter
    if (this.plantFilter) {
      filtered = filtered.filter((t) => t.plantId === this.plantFilter);
    }

    // Priority filter
    if (this.priorityFilter !== "all") {
      filtered = filtered.filter((t) => t.priority === this.priorityFilter);
    }

    // Vendor filter
    if (this.vendorFilter) {
      filtered = filtered.filter((t) =>
        t.vendorName?.toLowerCase().includes(this.vendorFilter.toLowerCase())
      );
    }

    // Search
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.entityNumber?.toLowerCase().includes(search) ||
          t.vendorName?.toLowerCase().includes(search) ||
          t.invoiceNumber?.toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (this.sortField) {
        case "dueDate":
          aVal = a.dueDate || "9999-99-99";
          bVal = b.dueDate || "9999-99-99";
          break;
        case "createdDate":
          aVal = a.createdDate || "";
          bVal = b.createdDate || "";
          break;
        case "totalAmount":
          aVal = a.totalAmount || 0;
          bVal = b.totalAmount || 0;
          break;
        case "vendorName":
          aVal = a.vendorName || "";
          bVal = b.vendorName || "";
          break;
        default:
          aVal = a.dueDate || "";
          bVal = b.dueDate || "";
      }

      if (this.sortDirection === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }

  get tasksByVendor(): Array<{ vendor: string; count: number; total: number }> {
    const vendorMap = new Map<string, { count: number; total: number }>();

    this.tasks
      .filter((t) => t.queueType === "payment" && t.status === "pending")
      .forEach((t) => {
        const vendor = t.vendorName || "Unknown";
        const existing = vendorMap.get(vendor) || { count: 0, total: 0 };
        vendorMap.set(vendor, {
          count: existing.count + 1,
          total: existing.total + (t.totalAmount || 0),
        });
      });

    return Array.from(vendorMap.entries())
      .map(([vendor, data]) => ({ vendor, ...data }))
      .sort((a, b) => b.total - a.total);
  }

  get totalPendingCount(): number {
    return this.tasks.filter(
      (t) => t.queueType === "payment" && t.status === "pending"
    ).length;
  }

  get totalPendingAmount(): number {
    return this.tasks
      .filter((t) => t.queueType === "payment" && t.status === "pending")
      .reduce((sum, t) => sum + (t.totalAmount || 0), 0);
  }

  get overdueCount(): number {
    return this.tasks.filter(
      (t) =>
        t.queueType === "payment" &&
        t.status === "pending" &&
        t.dueDate &&
        t.dueDate < this.today
    ).length;
  }

  get overdueAmount(): number {
    return this.tasks
      .filter(
        (t) =>
          t.queueType === "payment" &&
          t.status === "pending" &&
          t.dueDate &&
          t.dueDate < this.today
      )
      .reduce((sum, t) => sum + (t.totalAmount || 0), 0);
  }

  get dueThisWeekCount(): number {
    return this.tasks.filter(
      (t) =>
        t.queueType === "payment" &&
        t.status === "pending" &&
        t.dueDate &&
        t.dueDate >= this.today &&
        t.dueDate <= this.endOfWeek
    ).length;
  }

  get paidTodayCount(): number {
    return this.tasks.filter(
      (t) =>
        t.queueType === "payment" &&
        t.status === "completed" &&
        t.completedDate === this.today
    ).length;
  }

  get paidTodayAmount(): number {
    return this.tasks
      .filter(
        (t) =>
          t.queueType === "payment" &&
          t.status === "completed" &&
          t.completedDate === this.today
      )
      .reduce((sum, t) => sum + (t.totalAmount || 0), 0);
  }

  // ==================== Filter Actions ====================

  setPlantFilter = (plantId: number | null) => {
    this.plantFilter = plantId;
  };

  setVendorFilter = (vendor: string) => {
    this.vendorFilter = vendor;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setStatusFilter = (status: "all" | "pending" | "completed") => {
    this.statusFilter = status;
  };

  setPriorityFilter = (priority: "all" | "urgent" | "normal") => {
    this.priorityFilter = priority;
  };

  setDueDateFilter = (
    filter: "all" | "overdue" | "thisWeek" | "thisMonth"
  ) => {
    this.dueDateFilter = filter;
  };

  setSort = (
    field: "dueDate" | "totalAmount" | "vendorName" | "createdDate"
  ) => {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
  };

  clearFilters = () => {
    this.plantFilter = null;
    this.vendorFilter = "";
    this.searchTerm = "";
    this.statusFilter = "pending";
    this.priorityFilter = "all";
    this.dueDateFilter = "all";
  };

  // ==================== Selection ====================

  toggleTaskSelection = (taskId: number) => {
    const index = this.selectedTaskIds.indexOf(taskId);
    if (index >= 0) {
      this.selectedTaskIds.splice(index, 1);
    } else {
      this.selectedTaskIds.push(taskId);
    }
  };

  selectAllVisible = () => {
    this.selectedTaskIds = this.filteredTasks
      .filter((t) => t.status === "pending")
      .map((t) => t.taskId);
  };

  clearSelection = () => {
    this.selectedTaskIds = [];
  };

  // ==================== Actions ====================

  openPaymentWizard = (taskId?: number) => {
    this.rootStore.routerStore.goTo("POPaymentWizard");
  };

  // ==================== Data Loading ====================

  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load plants
      if ((this.rootStore as any).plantsStore) {
        await (this.rootStore as any).plantsStore.find();
      }

      // Load tasks
      if ((this.rootStore as any).approvalQueueStore) {
        await (this.rootStore as any).approvalQueueStore.find({
          query: { queueType: "payment" },
        });
      }

      runInAction(() => {
        this.plants = (this.rootStore as any).plantsStore?.entities || [];
        this.tasks = (this.rootStore as any).approvalQueueStore?.entities || [];
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load data";
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.clearFilters();
    this.selectedTaskIds = [];
    this.error = null;
  };
}

export default PaymentQueueStore;
